import axios from 'axios';
import { ChatGroup, Message, AIModel, User, Chat, ModelSettingsResponse, ModelSettings } from './types';

const API_BASE_URL = 'https://api.gpt4chats.ru/api';
axios.defaults.withCredentials = true;


export const createPayment = async (amount: number): Promise<{ 
  payment_id: string; 
  amount: number; 
  confirmation_url: string;
  yookassa_payment_id: string;
}> => {
  const response = await axios.post(`${API_BASE_URL}/payments/create/`, { amount });
  return response.data;
};

export const checkPaymentStatus = async (paymentId: string): Promise<{
  payment_id: string;
  status: string;
  amount: number;
  created_at: string;
}> => {
  const response = await axios.get(`${API_BASE_URL}/payments/status/${paymentId}/`);
  return response.data;
};

export const fetchChatMessages = async (chatUuid: string): Promise<Message[]> => {
  const response = await axios.get(`${API_BASE_URL}/chats/${chatUuid}/`);
  return response.data.messages;
};

export const createChat = async (): Promise<{ uuid: string; title: string }> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/chats/create/`); // Добавлен слэш
    const { chat_uuid, name } = response.data;
    return { uuid: chat_uuid, title: name };
  } catch (error) {
    console.error('Ошибка при создании чата:', error);
    throw error;
  }
};

export const renameChat = async (chatUuid: string, title: string): Promise<Chat> => {
  const response = await axios.put(`${API_BASE_URL}/chats/${chatUuid}/rename/`, { title });
  return response.data;
};

export const deleteChat = async (chatUuid: string): Promise<void> => {
  await axios.delete(`${API_BASE_URL}/chats/${chatUuid}/delete/`);
};

export const fetchModels = async (): Promise<AIModel[]> => {
  const response = await axios.get(`${API_BASE_URL}/models/`);
  return response.data;
};

export const fetchUser = async (): Promise<User> => {
  const response = await axios.get(`${API_BASE_URL}/user/`);
  return response.data.user;
};

export const startGeneration = async (
  chatUuid: string,
  content: FormData
): Promise<{ user_message_id: string; message_id: string; model: string }> => {
  const response = await axios.post(`${API_BASE_URL}/start_generation/`, content, { // Добавлен слэш
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const regenerateMessage = async (messageId: string): Promise<{ message_id: string; model: string }> => {
  const response = await axios.post(`${API_BASE_URL}/regenerate/${messageId}/`); // Добавлен слэш
  return response.data;
};

export const editUserMessage = async (
  messageId: string,
  content: string
): Promise<{ user_message_id: string; message_id: string; model: string }> => {
  const response = await axios.put(`${API_BASE_URL}/edit_message/${messageId}/`, { content });
  return response.data;
};

export const fetchChats = async (): Promise<ChatGroup[]> => {
  const response = await axios.get(`${API_BASE_URL}/chats/`);
  return response.data;
};

export const getMessageStatus = async (messageId: string): Promise<{
  id: string;
  content: string;
  is_complete: boolean;
  tokens: number;
  cost: number;
  model: string;
  attachedFiles: { name: string; data: string; size: number; type: string }[];
  children: Message[];
}> => {
  const response = await axios.get(`${API_BASE_URL}/message_status/${messageId}/`);
  return response.data;
};

export const fetchModelSettings = async (): Promise<ModelSettingsResponse> => {
  const response = await axios.get(`${API_BASE_URL}/model_settings/`);
  return response.data;
};

export const saveModelSettings = async (settings: ModelSettings): Promise<ModelSettingsResponse> => {
  const response = await axios.post(`${API_BASE_URL}/model_settings/save/`, settings); // Добавлен слэш
  return response.data.settings;
};

export interface ForgotPasswordResponse {
  success?: boolean;
  message?: string;
}

export interface ResetPasswordResponse {
  success?: boolean;
  message?: string;
}

export interface LoginResponse {
  success?: boolean;
  message?: string;
  email?: string;
  is_active?: boolean; // Добавлено
}

export interface RegisterResponse {
  success?: boolean;
  message?: string;
}

export interface ConfirmCodeResponse {
  success?: boolean;
  message?: string;
}

export interface ResendCodeResponse {
  success: boolean;
  message?: string;
  time_left?: number;
}

export interface CodeInfoResponse {
  time_left: number;
}

export const forgotPassword = async (email: string, hcaptchaToken: string): Promise<ForgotPasswordResponse> => {
  const response = await axios.post(
    `${API_BASE_URL}/forgot-password/`,
    {
      email,
      'h-captcha-response': hcaptchaToken,
    },
    { withCredentials: true }
  );
  return response.data;
};

export const resetPassword = async (
  email: string,
  code: string,
  password: string,
): Promise<ResetPasswordResponse> => {
  const response = await axios.post(
    `${API_BASE_URL}/reset-password/`,
    {
      email,
      code,
      password,
    },
    { withCredentials: true }
  );
  return response.data;
};

export const loginUser = async (
  email: string,
  password: string,
  hcaptchaToken: string
): Promise<LoginResponse> => {
  const response = await axios.post(
    `${API_BASE_URL}/login/`, // Добавлен слэш
    {
      email,
      password,
      'h-captcha-response': hcaptchaToken,
    },
    { withCredentials: true }
  );
  return response.data;
};

export const registerUser = async (email: string, password: string, hcaptchaToken: string) => {
  const response = await axios.post(`${API_BASE_URL}/register/`, {
    email,
    password,
    'h-captcha-response': hcaptchaToken,
  });
  return response.data;
};

export const confirmCode = async (email: string, code: string) => {
  const response = await axios.post(`${API_BASE_URL}/confirm-code/`, { email, code });
  return response.data;
};
export const resendResetCode = async (email: string): Promise<ResendCodeResponse> => {
  const response = await axios.post(
    `${API_BASE_URL}/resend-reset-code/`,
    { email },
    { withCredentials: true }
  );
  return response.data;
};

export const resendCode = async (email: string) => {
  const response = await axios.get(`${API_BASE_URL}/resend-code/`, {
    params: { email },
  });
  return response.data;
};

export const getCodeInfo = async (email: string) => {
  const response = await axios.get(`${API_BASE_URL}/get-code-info/`, {
    params: { email },
  });
  return response.data;
};

export const logoutUser = async (): Promise<void> => {
  try {
    await axios.post(`${API_BASE_URL}/logout/`, {}, { withCredentials: true }); // Добавлен слэш
  } catch (error) {
    console.error('Ошибка при выходе:', error);
    throw error;
  }
};

export const setUserModel = async (modelSystemName: string): Promise<{ message: string; current_model: string }> => {
  const response = await axios.post(`${API_BASE_URL}/user/set_model/`, { model_system_name: modelSystemName }); // Добавлен слэш
  return response.data;
};

export const getUsageStatistics = async (): Promise<{ statistics: { model__display_name: string; tokens_used: number; cost: number; timestamp: string }[] }> => {
  const response = await axios.get(`${API_BASE_URL}/statistics/`);
  return response.data;
};

// Новая функция для Google OAuth
export const initiateGoogleLogin = async (): Promise<{ redirect_url: string }> => {
  const response = await axios.get(`${API_BASE_URL}/login/google/`, { withCredentials: true });
  return response.data;
};